import React, { type ReactNode, useCallback } from 'react';
import Button from '@atlaskit/button';
import { Text } from '@atlaskit/primitives';
import { useIntl } from '@atlassian/jira-intl';
import { useAnalyticsEvents, fireUIAnalytics } from '@atlassian/jira-product-analytics-bridge';
import { AccessTokenModalType } from '../../../../../common/constants/access-token';
import type { SupportedThirdParty } from '../../../../../common/constants/import-type';
import { useContentForThirdParty } from '../../../../../common/utils';
import { useThirdPartyState } from '../../../../../controllers/import-state';
import { useAccessTokenModalState } from '../../../../../controllers/ui-state';

export type EditPersonalAccessTokenProps = {
	thirdParty: SupportedThirdParty;
};

export const EditPersonalAccessToken = ({ thirdParty }: EditPersonalAccessTokenProps) => {
	const { changeCurrentAccount } = useContentForThirdParty(thirdParty);
	const [, { setThirdPartyState }] = useThirdPartyState();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { formatMessage } = useIntl();

	const [, { setRequestAuthenticationToken }] = useAccessTokenModalState();

	const handleChangeAccount = useCallback(() => {
		const analyticsEvent = createAnalyticsEvent({
			action: 'clicked',
			actionSubject: `${thirdParty}ChangeAccountButton`,
		});
		fireUIAnalytics(analyticsEvent);
		setRequestAuthenticationToken({
			thirdParty,
			modalType: AccessTokenModalType.EDIT_TOKEN,
		}).then((isSaved) => {
			if (isSaved) {
				setThirdPartyState({
					selectedWorkspaceId: null,
					selectedFolderId: null,
					selectedSubfolderId: null,
					selectedEntities: null,
					selectedEntityId: null,
					selectedEntityName: null,
					selectedEntityStyle: null,
					workspaces: null,
					folders: null,
					entities: null,
					entityStats: null,
					members: null,
					thirdPartyFieldConfigState: null,
					siteName: null,
				});
			}
		});
	}, [createAnalyticsEvent, thirdParty, setRequestAuthenticationToken, setThirdPartyState]);

	if (!changeCurrentAccount) {
		return null;
	}

	return (
		<Text size="UNSAFE_small">
			{formatMessage(changeCurrentAccount, {
				link: (chunks: ReactNode[]) => (
					<Button appearance="link" spacing="none" onClick={handleChangeAccount}>
						{chunks}
					</Button>
				),
			})}
		</Text>
	);
};
